import React from 'react';
import { HERO_IMAGE } from '../data/heroImage';

/**
 * The hero photograph, as one or three parallax planes.
 *
 * Layer count is decided by whatever scripts/prepare_hero.py last produced.
 * Every source it emits is responsive, so the browser picks a width instead of
 * downloading a 1600px plate for a 400px card.
 */

const SIZES = '(min-width: 1024px) 400px, (min-width: 640px) 60vw, 90vw';

function srcSet(records, kind) {
  if (!records || !records.length) return undefined;
  const parts = records
    .filter((r) => r[kind])
    .map((r) => `${r[kind]} ${r.w}w`);
  return parts.length ? parts.join(', ') : undefined;
}

function largest(records, kind) {
  if (!records || !records.length) return undefined;
  const usable = records.filter((r) => r[kind]);
  if (!usable.length) return undefined;
  return usable[usable.length - 1][kind];
}

function Plane({ records, alt, priority, style, className, kind = 'webp' }) {
  const fallbackKind = kind === 'webp' && records && records.some((r) => r.jpg) ? 'jpg' : kind;
  const src = largest(records, fallbackKind) || largest(records, 'webp');

  return (
    <img
      src={src}
      srcSet={srcSet(records, fallbackKind)}
      sizes={SIZES}
      alt={alt}
      aria-hidden={alt ? undefined : 'true'}
      width="960"
      height="1200"
      decoding="async"
      loading={priority ? 'eager' : 'lazy'}
      fetchpriority={priority ? 'high' : undefined}
      style={style}
      className={className}
    />
  );
}

export default function HeroImage({ layerStyle }) {
  const plane = 'parallax-layer absolute -inset-[7%] w-[114%] h-[114%] object-cover';

  if (!HERO_IMAGE.layered) {
    return (
      <Plane
        records={HERO_IMAGE.flat}
        alt={HERO_IMAGE.alt}
        priority
        style={layerStyle(0.6)}
        className={plane}
      />
    );
  }

  return (
    <>
      <Plane records={HERO_IMAGE.wall} alt="" style={layerStyle(0.35)} className={plane} />
      <Plane
        records={HERO_IMAGE.figure}
        alt={HERO_IMAGE.alt}
        priority
        style={layerStyle(1)}
        className={plane}
      />

      {/* The sway plane only renders when a hand-masked dupatta tail exists.
          Reusing the figure here would draw the whole garment twice and ghost
          it against itself — two planes with no sway beats three with a
          double image. */}
      {HERO_IMAGE.dupatta && (
        // Sway rotate on the wrapper, parallax translate on the image inside.
        // One transform per element, or the inline style wins and the sway
        // silently never runs.
        <div className="dupatta-sway animate-sway absolute inset-0 pointer-events-none">
          <Plane records={HERO_IMAGE.dupatta} alt="" style={layerStyle(1.75)} className={plane} />
        </div>
      )}
    </>
  );
}
